import { Link } from "react-router-dom";
import { ShieldCheck, Clock, Heart } from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef } from "react";
import heroBackground from "@/assets/hero-bsofms-v2.jpg";
import { letterRise, riseFade, splitChars, easeStandard } from "@/lib/motion";
import { trackEvent, MARKETING_EVENTS } from "@/lib/observability";
import { scrollToSection } from "@/lib/scrollToSection";
import { openRequestAccess } from "./RequestAccessDialog";
import { EXPERIMENTS, CTA_VARIANT_COPY, getVariant, trackExposure } from "@/lib/experiment";
import { CtaButton } from "@/components/cta/CtaButton";
import { CtaGroup } from "@/components/cta/CtaGroup";
import PictureImg from "@/components/ui/PictureImg";

const LINE_1 = "Learn your energy patterns";
const LINE_2 = "Plan with more context.";

const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement | null>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "12%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.06]);
  const bgFade = useTransform(scrollYProgress, [0, 0.7, 1], [1, 0.7, 0.5]);

  useEffect(() => {
    if (typeof window === "undefined" || !ref.current) return;
    const el = ref.current;
    let fired = false;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !fired) {
            fired = true;
            trackEvent(MARKETING_EVENTS.heroPromiseSeen, {
              ratio: Number(entry.intersectionRatio.toFixed(2)),
            });
            io.disconnect();
          }
        }
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const ctaVariant = getVariant(EXPERIMENTS.heroCtaV1, ["A", "B"] as const);
  useEffect(() => {
    trackExposure(EXPERIMENTS.heroCtaV1, ctaVariant);
  }, [ctaVariant]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden bg-background"
      aria-labelledby="hero-heading"
    >
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 z-0 will-change-transform"
        style={{ y: bgY, scale: bgScale, opacity: bgFade }}
      >
        <PictureImg
          src={heroBackground}
          optimizedBase="/opt/hero-bsofms-v2"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          width={1920}
          height={1080}
          sizes="100vw"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/55 via-background/40 to-background/90" />
        <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-background via-background/85 to-transparent" />
        <div
          aria-hidden="true"
          className="absolute inset-0 [background:radial-gradient(ellipse_at_center,hsl(var(--background)/0.35),transparent_60%)]"
        />
      </motion.div>

      <div className="container mx-auto px-6 sm:px-10 lg:px-16 pt-32 pb-32 relative z-10">
        <div className="max-w-5xl mx-auto text-center space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05, ease: easeStandard }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background/70 backdrop-blur-sm border border-foreground/10 text-[11px] tracking-[0.14em] uppercase text-muted-foreground"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
            A lower-burden wellness tool for adult women living with MS
          </motion.div>

          <motion.h1
            id="hero-heading"
            initial="hidden"
            animate="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.022, delayChildren: 0.15 } } }}
            className="font-display text-foreground text-display-2xl [text-wrap:balance] [hyphens:none] [overflow-wrap:normal] [word-break:normal]"
            aria-label={`${LINE_1} ${LINE_2}`}
          >
            <span className="block">
              {LINE_1.split(" ").map((word, wi, arr) => (
                <span key={`w1-${wi}`} className="inline-block whitespace-nowrap align-baseline">
                  {splitChars(word).map(({ char, key }) => (
                    <motion.span key={key} variants={letterRise} className="inline-block">{char}</motion.span>
                  ))}
                  {wi < arr.length - 1 ? "\u00A0" : null}
                </span>
              ))}
            </span>
            <span className="block italic text-primary mt-1">
              {LINE_2.split(" ").map((word, wi, arr) => (
                <span key={`w2-${wi}`} className="inline-block whitespace-nowrap align-baseline">
                  {splitChars(word).map(({ char, key }) => (
                    <motion.span key={key} variants={letterRise} className="inline-block">{char}</motion.span>
                  ))}
                  {wi < arr.length - 1 ? "\u00A0" : null}
                </span>
              ))}
            </span>
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={riseFade}
            transition={{ delay: 0.65, duration: 0.7, ease: easeStandard }}
            className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto font-body"
          >
            Use a brief self-check-in and a retrospective weekly view to review what you logged over time. Myelina does not predict tomorrow, diagnose a cause, recommend treatment, or replace your care team.
          </motion.p>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={riseFade}
            transition={{ delay: 0.7, duration: 0.7, ease: easeStandard }}
            className="text-sm sm:text-base text-foreground/80 max-w-lg mx-auto font-body italic"
          >
            Your entries may reveal tendencies worth reviewing. They may be incomplete, and they do not prove cause.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={riseFade}
            transition={{ delay: 0.75, duration: 0.7, ease: easeStandard }}
            className="pt-2"
          >
            <CtaGroup microcopyFor="requestAccess">
              <CtaButton
                data-test="hero-request-access"
                intent="requestAccess"
                source="hero"
                experiment={{ key: EXPERIMENTS.heroPrimaryCtaV1, variants: CTA_VARIANT_COPY[EXPERIMENTS.heroPrimaryCtaV1] }}
                analyticsMetadata={{ signup: true, destination: "request-access", experiments: { [EXPERIMENTS.heroCtaV1]: ctaVariant } }}
                onClick={() => openRequestAccess("hero")}
                className="w-full sm:w-auto px-8"
              />
              <CtaButton
                data-test="hero-see-demo"
                intent="seeDemo"
                source="hero"
                experiment={{ key: EXPERIMENTS.heroSecondaryCtaV1, variants: CTA_VARIANT_COPY[EXPERIMENTS.heroSecondaryCtaV1] }}
                analyticsMetadata={{ destination: "#try-checkin", experiments: { [EXPERIMENTS.heroCtaV1]: ctaVariant } }}
                onClick={(e) => {
                  e.preventDefault();
                  void scrollToSection("try-checkin");
                }}
                className="w-full sm:w-auto px-8 bg-background/70 backdrop-blur-sm border-foreground/25 text-foreground hover:bg-background/85"
              />
            </CtaGroup>
          </motion.div>

          <p className="text-[11px] text-muted-foreground text-center pt-1 max-w-md mx-auto leading-relaxed">
            Pilot access is by invitation. Eligibility, current product limits, and the data practices for each product version are provided in writing before onboarding.
          </p>

          <p className="text-[11px] text-muted-foreground text-center pt-1 max-w-md mx-auto leading-relaxed">
            <Link to="/privacy" className="underline hover:text-foreground">Privacy</Link>
            {" · "}
            <Link to="/security" className="underline hover:text-foreground">Security and data use</Link>
          </p>

          <motion.nav
            initial="hidden"
            animate="visible"
            variants={riseFade}
            transition={{ delay: 0.8, duration: 0.7, ease: easeStandard }}
            aria-label="Jump to section"
            className="flex flex-wrap justify-center gap-2 sm:gap-3"
          >
            {[
              { href: "#features", label: "Features" },
              { href: "#how-it-works", label: "How it works" },
            ].map(({ href, label }) => (
              <a
                key={href}
                href={href}
                onClick={(e) => {
                  e.preventDefault();
                  void scrollToSection(href.slice(1));
                }}
                className="px-4 py-2 rounded-full bg-background/60 backdrop-blur-sm border border-foreground/10 text-[13px] text-foreground hover:bg-background/85 hover:border-foreground/20 transition-colors"
              >
                {label}
              </a>
            ))}
          </motion.nav>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={riseFade}
            transition={{ delay: 0.85, duration: 0.7, ease: easeStandard }}
            className="text-xs text-muted-foreground/90 -mt-2"
          >
            By invitation · Small pilot waves · A genuine right to decline
          </motion.p>

          <motion.ul
            initial="hidden"
            animate="visible"
            variants={riseFade}
            transition={{ delay: 0.95, duration: 0.7, ease: easeStandard }}
            aria-label="What you can expect"
            className="mx-auto mt-6 flex flex-wrap justify-center items-center gap-x-6 gap-y-3 text-[12px] text-muted-foreground max-w-2xl"
          >
            <li className="inline-flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
              <span>Adjustable check-in effort</span>
            </li>
            <li className="inline-flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
              <span>Written privacy and product limits</span>
            </li>
            <li className="inline-flex items-center gap-2">
              <Heart className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
              <span>No-wearable pathway</span>
            </li>
          </motion.ul>
        </div>
      </div>

      <motion.div
        aria-hidden="true"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        style={{ opacity: useTransform(scrollYProgress, [0, 0.15], [1, 0]) }}
      >
        <span className="text-[10px] tracking-[0.32em] uppercase text-muted-foreground">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-muted-foreground/40 to-transparent" />
      </motion.div>
    </section>
  );
};

export default Hero;
